import { romanToInt } from 'shared/definitions/musicTheory/romanNumerals';
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import { clefNames } from 'shared/definitions/musicTheory/clefs';
import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import utils from '../utils';
import * as Skills from '../uTheory.Skills/skills';
import * as Notation from '../notation';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import RomanNumeral from '../uTheory.Notation/romanNumeral';
import * as Font from '../uTheory.Font/font';
import type { ChordIdAnswer } from './chordIdButtons';

export type RomanNumeralIDQuestion = {
  rn: RomanNumeral;
  clef?: ClefName;
  skills: string[];
  text: string[];
};

interface RomanNumeralIDOptions extends InteractiveOptions {
  spacing?: 'close' | 'open';
  inversion?: boolean;
  chordTypes?: 'triads' | 'sevenths' | 'both';
  questions?: string[];
  skills?: (string | number)[];
  displayInstructions?: boolean;
}

type RomanNumeralIDAnswerData = {
  correct: boolean;
  correctNumeral: boolean;
  correctQuality: boolean;
  correctFigures: boolean;
  correctSeventh: boolean;
  validFigures: boolean;
  studentAnswerHTML: string;
  noteHolder: Notation.NoteHolder | null;
  question: RomanNumeralIDQuestion;
  answer: ChordIdAnswer;
};

const BASS = 1;
const TREBLE = 0;

function getFeedbackString(context: RomanNumeralIDAnswerData): string {
  const { correctFigures, correctNumeral, correctQuality } = context;
  const { correctSeventh, validFigures, question, answer } = context;
  const warn = [];
  // warn of any invalidity first:
  if (!validFigures) warn.push('Those figures are not possible for any triad or seventh chord.');
  if (romanToInt(answer.roman.numeral) === false) {
    warn.push(
      `<span class='accidentals'>${answer.roman.numeral}</span> is not a possible roman numeral.`
    );
  }

  // now warn of any errors:
  if (!correctNumeral) warn.push('Wrong root for the roman numeral.');
  if (!correctQuality) warn.push('Wrong chord quality.');
  if (!correctSeventh) {
    warn.push(
      `The chord is ${question.rn.isSeventh ? '' : 'not '} a seventh, but your figures are.`
    );
  }
  if (!correctFigures) {
    warn.push('Wrong inversion');
  }

  return warn[0] || '';
}

/**
 * Interactive for identifying roman numerals on the staff: "Draw G Maj: ii65"
 */
export default class RomanNumeralID extends InteractivePrototype {
  declare questions: RomanNumeralIDQuestion[];

  declare currentQuestion: RomanNumeralIDQuestion;

  declare options: RomanNumeralIDOptions;

  skillsCalculator!: SkillCalculator;

  answerDiv!: HTMLDivElement;

  keySig!: Notation.KeySignature;

  staves!: {
    staff: Notation.Staff;
    voice: Notation.Voice;
    noteHolder: Notation.NoteHolder;
    clef: Notation.Clef;
  }[];

  maxQuestions: number | undefined;

  answerDivId: string;

  qg!: QuestionGenerator;

  studentAnswerHTML: string;

  chord!: Notation.Chord;

  constructor(displayDiv: HTMLDivElement, options: Partial<RomanNumeralIDOptions> = {}) {
    const skills = (options.skills || []).map(Skills.makeString);
    options.spacing =
      options.spacing || (skills.find((s) => s === 'rn_open_spacing') ? 'open' : 'close');

    if (options.inversion === undefined && options.drill) {
      options.inversion = skills.some((s) => s.includes('inversion'));
    } else if (options.inversion === undefined) {
      options.inversion = true;
    }

    const triads = !!skills.find((s) => s.includes('triad'));
    const sevenths = !!skills.find((s) => s.includes('seventh'));
    if (!options.chordTypes) {
      if (sevenths && !triads) {
        options.chordTypes = 'sevenths';
      } else if (triads && !sevenths) {
        options.chordTypes = 'triads';
      } else {
        if (!triads && !sevenths) {
          console.warn(
            'options.chordTypes should be specified manually or determinable by skills.'
          );
        }
        options.chordTypes = 'both';
      }
    }

    super(displayDiv, options);
    this.targetSpeedFromSkills = RomanNumeralID.registry
      .map((r) => r.skills)
      .reduce((a, b) => a.concat(b));
    this.tempo = 180;

    this.layout = {
      answer: {
        percent: 10,
        visible: true,
      },
      notation: {
        percent: options.spacing === 'open' ? 55 : 45,
      },
      instructions: {
        percent: 10,
      },
    };

    this.answerDivId = '';
    this.studentAnswerHTML = '';
    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: RomanNumeralIDOptions): Promise<void> {
    this.settings = {
      instructions: true,
      notation: {
        fixedWidth: 180,
        ariaTitle: {
          rhythm: false,
          barline: false,
        },
        ariaLive: 'alert',
      },
      editor: false,
      letters: {
        chordIdButtons: {
          romans: true,
          inversionType: options.inversion ? 'figures' : false,
          chordTypes: options.chordTypes!,
          quality: false,
          root: false,
          numUnderscoresForBlankQuestion: 4,
          checkButtonText: options.testMode ? 'Submit' : 'Check',
        },
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
      uPrefs: { appName: 'romanNumeralID', optional: false },
      instructionsPanel: {
        innerHTML: '',
        onInteractiveReady: (textDiv, buttonDiv, button) => {
          const knowsHowToType = this.uPrefs
            ? this.uPrefs.getPreference('knowsHowToType')
            : undefined;
          const lastKnewHowToType = this.uPrefs
            ? this.uPrefs.getPreference('lastKnewHowToType')
            : undefined;
          const expertTyper = knowsHowToType > 10;
          const recentTyper =
            lastKnewHowToType &&
            new Date().getTime() - new Date(lastKnewHowToType).getTime() < 24 * 60 * 60 * 1000;

          const forceInstructions = options.displayInstructions;
          const forceShowInstructions = forceInstructions && options.displayInstructions;
          const forceHideInstructions = forceInstructions && !options.displayInstructions;

          const hideInstructions =
            forceHideInstructions || (!forceShowInstructions && (expertTyper || recentTyper));

          if (hideInstructions) {
            this.instructionsPanelDiv!.innerHTML = '';
            this.instructionsPanelDiv!.style.opacity = '0';
            this.fadeInstructionsPanel(button, this.instructionsPanelDiv);
            this.loadingPromise.then(() => this.startTimerAndListeners());
            return;
          }
          this.instructionsPanelDiv!.style.transition = 'opacity 1s';
          this.instructionsPanelDiv!.style.opacity = '1';

          const up = Font.toHTML('symbol', 'up');
          const i = Font.toHTML('romanNumeral', 'i');
          const I = Font.toHTML('romanNumeral', 'I');
          const v = Font.toHTML('romanNumeral', 'v');
          const V = Font.toHTML('romanNumeral', 'V');
          textDiv.innerHTML = `
            <p>Identify the roman numeral you see.</p>
            <p>Press the <button disabled>${i}</button> and <button disabled>${v}</button> buttons like
            a keyboard. Use the <button disabled>${up}</button> button to toggle upper and lower case roman numerals.</p>

            <p>Example: for ${I + V} you'd type <button disabled>${up}</button>,
            <button disabled>${i}</button>, <button disabled>${v}</button>.</p>
          `;
        },
      },
    };

    this.skillsCalculator = new SkillCalculator();
    return super.init(displayDiv, options);
  }

  createCustomDivs(): void {
    this.createDiv('answerDiv', { textAlign: 'center' });
    this.answerDiv.style.paddingBottom = '0.4em';
    this.layout.answer!.div = this.answerDiv;
    this.layout.answer!.callback = (height) => this.sizeTextDiv(height, this.layout.answer!.div!);
    this.displayDiv.removeChild(this.answerDiv);
    this.displayDiv.insertBefore(this.answerDiv, this.lettersDiv!);
  }

  /**
   * initialize the this.music Music object
   */
  initializeMusic(): void {
    const makeStaff = (clefName: ClefName) => {
      const clef = new Notation.Clef(clefName);
      const staff = new Notation.Staff(clef);
      const nh = new Notation.NoteHolder('', 'w');
      // nh.ghostIfEmpty = true; Attribute doesn't exist.
      const measure = new Notation.Measure(nh);
      const voice = new Notation.Voice(measure);
      voice.setStaff(staff);
      return {
        staff,
        voice,
        noteHolder: nh,
        clef,
      };
    };

    this.keySig = new Notation.KeySignature('C', 'Major');
    this.staves =
      this.options.spacing === 'open'
        ? (['treble', 'bass'] as ClefName[]).map(makeStaff)
        : (['treble'] as ClefName[]).map(makeStaff);

    const musicElements = this.staves.reduce(
      (arr: (Notation.KeySignature | Notation.Staff | Notation.Voice)[], staff) =>
        arr.concat([this.keySig, staff.staff, staff.voice]),
      []
    );

    this.music = new Notation.Music(musicElements);
  }

  /**
   * Generate questions from skills or questions array
   * @param questions - "Bb:ii42/bass", omit clef if open spacings.
   */
  generateQuestions(questions: string[] | false = false): void {
    this.qg = new QuestionGenerator();

    this.questions = this.options.drill
      ? this.generateDrillQuestions()
      : // Both checkpoint & regular:
        this.processManualQuestions(questions);

    this.maxQuestions = this.questions.length;
    this.answerDivId = `rnid${utils.uuid}`;
    this.questionPrompt = 'Identify the Roman Numeral<br />';
    if (!this.options.testMode)
      this.questionPrompt += `<span style="font-size: 0.5em">{question} of ${this.questions.length}</pan>`;
  }

  generateDrillQuestions(): RomanNumeralIDQuestion[] {
    const skills = (this.options.skills || []).map(Skills.makeString);
    const open = this.options.spacing === 'open' || skills.find((skill) => skill.includes('open'));
    return this.qg
      .roman_numerals(this.options.maxQuestions || 8, skills, {
        includeSpacingSkills: true,
        noRaiseTheLeadingTone: true,
      })
      .map(
        (q: {
          rn: RomanNumeral;
          skills: string[];
        }): { rn: RomanNumeral; skills: string[]; clef?: ClefName } => {
          if (!open) {
            return { ...q, clef: (['treble', 'bass'] as ClefName[])[~~(Math.random() * 2)] };
          }
          return q;
        }
      )
      .map(this.processQuestion);
  }

  processManualQuestions(
    questions: string[] | [string, { removeMixture: boolean }][] | false
  ): RomanNumeralIDQuestion[] {
    questions = questions || this.options.questions || [];
    return questions
      .map((question) => {
        let questionString: string;
        let options: { removeMixture?: boolean } = {};
        if (utils.isArray(question)) {
          questionString = question[0];
          options = question[1];
        } else {
          questionString = question;
        }
        // console.log(`Processing questionString: ${questionString}, options: ${options}`);

        let clef: ClefName | undefined = clefNames.find((clefName) =>
          questionString.includes(clefName)
        );
        if (clef) {
          questionString = questionString.slice(0, questionString.indexOf(clef) - 1);
        } else {
          clef = (['treble', 'bass'] as ClefName[])[~~(Math.random() * 2)] as ClefName;
        }
        const rn = new RomanNumeral(questionString);
        if (options.removeMixture) rn.removeMixture();

        const keyIsMajor = rn.key.quality === 'Major';
        const sd = rn.scaleDegree;
        const inversion = rn.inversion;
        const isSeventh = rn.isSeventh;
        const skills = this.qg.getRNSkills({
          sd,
          inversion,
          isSeventh,
          keyIsMajor,
          noRaiseTheLeadingTone: true,
        });
        skills.push(this.options.spacing === 'open' ? 'rn_open_spacing' : 'rn_close_spacing');

        return { rn, clef, skills };
      })
      .map(this.processQuestion);
  }

  // eslint-disable-next-line class-methods-use-this
  processQuestion(
    question: Omit<RomanNumeralIDQuestion, 'text'>,
    index: number
  ): RomanNumeralIDQuestion {
    return { ...question, text: [`${index + 1}`] };
  }

  newQuestion(): void {
    super.newQuestion();
    this.studentAnswerHTML = '_';
    this.letterPanel!.chordIdButtons!.reset();
    this.updateAnswerDisplay();
    this.staves.forEach((staff) => {
      staff.noteHolder.length = 0;
    }, this);
    const chord = this.currentQuestion.rn.chord;
    const keySig = this.currentQuestion.rn.key;
    this.keySig.set(keySig.toString());
    if (this.options.spacing === 'open') {
      const nh = chord.getOpenSpacing();
      nh.notes.forEach((note, index) => {
        const clefs = note.prefersClef();
        let staff;
        // if two possible clefs, assign it based on chorale spacing
        if (clefs.length > 1) {
          staff = index <= 1 ? BASS : TREBLE;
        } else {
          staff = clefs[0] === 'treble' ? TREBLE : BASS;
        }
        this.staves[staff]!.noteHolder.push(note.clone());
      });
    } else {
      // Figure out what octaves have a single clef that's possible:
      const octaves: { octave: number; clef: ClefName }[] = [];
      const questionClef = this.currentQuestion.clef ? [this.currentQuestion.clef] : undefined;
      [2, 3, 4, 5, 6].forEach((octave: number) => {
        chord.setOctave(octave);
        const clefs = chord.noteHolder.prefersClef(questionClef);
        if (clefs.length) {
          octaves.push({
            octave,
            clef: utils.shuffle(clefs)[0] as ClefName,
          });
        }
      });
      // Randomly choose among these & set octave & clef:
      chord.setOctave(utils.shuffle(octaves)[0]!.octave);
      this.staves[0]!.clef.setName(octaves[0]!.clef);
      // Add the notes:
      this.staves[0]!.noteHolder.notes = chord.noteHolder.notes.map((note) => note.clone());
    }
    this.notation!.render();
  }

  displayInstructions(): void {
    super.displayInstructions();
    const minMaj = this.currentQuestion.rn.key.quality;
    this.answerDiv.innerHTML = `${minMaj} key: <span id='${this.answerDivId}' style='white-space: nowrap;'>____</span>`;
  }

  removeNotesFromNoteHolder(count: number): Notation.NoteHolder {
    const noteHolder = this.chord.noteHolder;

    for (let i = 0; i < count; i++) {
      noteHolder.remove(noteHolder.notes[0]!);
    }

    return noteHolder;
  }

  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners() {
    // Code to set callbacks & communication to eventually check an answer.
    this.letterPanel!.addCustomButtonCallback((value) => {
      if (value === 'check') this.checkAnswer(false);
      else this.updateAnswerDisplay();
    }, this);
  }

  updateAnswerDisplay() {
    const answerString = this.letterPanel!.chordIdButtons!.answerText;
    this.studentAnswerHTML = answerString;
    // eslint-disable-next-line no-undef
    const answerSpan = document.getElementById(this.answerDivId);
    if (answerString === '____') {
      this.answerDiv.removeAttribute('aria-live');
    } else {
      this.answerDiv.setAttribute('aria-live', 'assertive');
    }
    answerSpan!.innerHTML = answerString;
  }

  checkAnswer(forceSubmit: boolean) {
    if (this.rightAnswerDelay && !forceSubmit) return undefined;
    const Chord = Notation.Chord;
    const question = this.currentQuestion;
    const dimCircle: string = question.rn.numeral
      .split('')
      .filter((c) => ['o', '/'].includes(c))
      .join('');
    const answer = this.letterPanel!.chordIdButtons!.answer;

    const incomplete = [];

    if (!answer.roman.numeral) incomplete.push('You must provide a roman numeral.');

    // Is the quality right?
    //  - is each uppercase/lowercase? do both have (or not) o or o/?
    const isLowerCase = (t: string) => t.toLowerCase() === t;
    const correctQuality =
      isLowerCase(answer.roman.numeral) === isLowerCase(question.rn.numeral) &&
      dimCircle === answer.roman.dimCircle;

    // Is the root right?
    //  - rn -> sd
    const normalizeNumeral = (t: string) =>
      t
        .toLowerCase()
        .split('')
        .filter((c) => ['v', 'i'].includes(c))
        .join('');
    const correctNumeral =
      normalizeNumeral(answer.roman.numeral) === normalizeNumeral(question.rn.numeral);

    // Are the figures right?
    const validFigures = Chord.isValidFigures(answer.inversion);
    const correctSeventh =
      validFigures && Chord.figuresAreSeventh(answer.inversion) === question.rn.isSeventh;
    const correctFigures =
      !this.options.inversion ||
      (correctSeventh && Chord.figuresToInversion(answer.inversion) === question.rn.inversion);

    if (!forceSubmit && incomplete.length) {
      return this.checkMark!.warn(incomplete[0]!);
    }

    const correct = correctNumeral && correctQuality && correctFigures;

    const answerAndDataObj: RomanNumeralIDAnswerData = {
      correct,
      correctNumeral,
      correctQuality,
      correctFigures,
      correctSeventh,
      validFigures,
      studentAnswerHTML: this.studentAnswerHTML,
      question,
      answer,
      noteHolder: null,
    };

    if (this.options.testMode && forceSubmit) {
      return this.getAnswerAndData(answerAndDataObj);
    }
    if (this.options.testMode) {
      return this.checkpointAnswer(answerAndDataObj);
    }

    if (correct) {
      if (this.questionNumber === 0) {
        let canType = this.uPrefs?.getPreference('knowsHowToType');
        canType = canType ? ++canType : 1;
        this.uPrefs?.setPreferences({
          knowsHowToType: canType,
          lastKnewHowToType: new Date(),
        });
      }
      this.rightAnswer(1100);
    } else if (this.wrongAnswerCount++) {
      this.wrongAnswer(getFeedbackString(answerAndDataObj));
    } else {
      this.wrongAnswer();
    }
    return undefined;
  }

  editorFeedback(answerObj: RomanNumeralIDAnswerData): void {
    const chord = this.currentQuestion.rn.chord;
    this.editor!.deselectAll();
    if (answerObj.noteHolder) {
      answerObj.noteHolder.forEach((note) => {
        if (!chord.noteHolder.noteExistsAtIgnoreOctave(note)) {
          note.color = 'red';
        }
      });
    }
    this.editor!.redraw();
  }

  getAnswerAndData(answerData: RomanNumeralIDAnswerData): AnswerAndData {
    if (!answerData) {
      return this.checkAnswer(true) as any;
    }

    const studentAnswerText = `${answerData.question.rn.key.toAccidentalFontString()}: ${
      answerData.studentAnswerHTML
    }`;

    return {
      question: 'Identify the roman numeral',
      questionType: 'RomanNumeralID',
      questionText: 'Identify the roman numeral',
      questionSVG: this.notation!.exportSVG(),
      studentAnswerText,
      correctAnswerText: answerData.question.rn.toString({ key: true, font: true }),
      rightAnswers: answerData.correct ? 1 : 0,
      wrongAnswers: answerData.correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };
  }

  gameOver() {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    try {
      this.displayDiv.removeChild(this.lettersDiv!);
    } catch (err) {
      // swallow it
    }
    super.gameOver();
  }

  static registry = [
    {
      description: 'ID Roman Numerals',
      skills: [
        'triad_rn_major',
        'triad_rn_minor',
        'seventh_rn_major',
        'seventh_rn_minor',
        'rn_inversion',
        'rn_close_spacing',
      ],
    },
    {
      description: 'ID Roman Numerals (grand staff)',
      skills: [
        'triad_rn_major',
        'triad_rn_minor',
        'seventh_rn_major',
        'seventh_rn_minor',
        'rn_inversion',
        'rn_open_spacing',
      ],
    },
  ];
}
